/**
 * Wrapper around the built-in Promise object, which exposes the resolve and
 * reject methods publicly.
 */
export class Deferred<T> implements PromiseLike<T> {
  /**
   * Internal reference to the resolve function created by the promise
   */
  private resolveRef: (value: T | PromiseLike<T>) => void;

  /**
   * Internal reference to the rejecty function created by the promise
   */
  private rejectRef: (reason?: any) => void; // tslint:disable-line no-any

  /**
   * Internal state variable that's used to keep track of whether or not the
   * promise object has been resolved yet.
   */
  private isPromiseResolved: boolean = false;

  /**
   * Internal state variable that's used to keep track of whether or not the
   * promise object has been rejected yet.
   */
  private isPromiseRejected: boolean = false;

  /**
   * Expose the isPromiseRejected value publicly in a way, where the clients
   * can only read it.
   */
  public get isResolved(): boolean {
    return this.isPromiseResolved;
  }

  /**
   * Expose the isPromiseRejected value publicly in a way, where the clients
   * can only read it.
   */
  public get isRejected(): boolean {
    return this.isPromiseRejected;
  }

  /**
   * Create the internal promise object, which we're using to back our
   * Deferred object internally, and cache the resolve and reject methods for
   * later use.
   */
  // tslint:disable-next-line promise-must-complete
  private readonly promise: Promise<T> = new Promise<T>((resolve, reject) => {
    this.resolveRef = resolve;
    this.rejectRef = reject;
  });

  /**
   * Propagate calls directly to the internal promise object
   */
  public async then<TResult1, TResult2 = never>(
    onfulfilled:
      | ((value: T) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?:
      | ((reason?: unknown) => TResult2 | PromiseLike<TResult2>)
      | null
      | undefined
  ): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }

  /**
   * Propagate calls directly to the internal promise object
   */
  // tslint:disable-next-line no-reserved-keywords
  public async catch<TResult>(
    onrejected:
      | ((reason?: unknown) => TResult | PromiseLike<TResult>)
      | null
      | undefined
  ): Promise<T | TResult> {
    return this.promise.catch(onrejected);
  }

  /**
   * Exposes the resolve method publicly on our deferred object
   */
  public resolve(value: T | PromiseLike<T>): void {
    this.isPromiseResolved = true;
    this.resolveRef(value);
  }

  /**
   * Exposes the reject method publicly on our deferred object
   */
  public reject(reason?: unknown): void {
    this.isPromiseRejected = true;
    this.rejectRef(reason);
  }
}
